import axios from 'axios'
import type { CancelTokenSource } from 'axios'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Empty, Spin } from 'antd'
import {
  FilterOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import { Button, message } from '@surecompanies/core_components'

import VcnManagementCardTable from '../../components/VcnManagement/VcnManagementCardTable'
import { cardKeyOf } from '../../components/VcnManagement/vcnManagementCardKey'
import { VcnManagementCardSidebar } from '../../components/VcnManagement/VcnManagementCardSidebar'
import { VcnManagementSearchField } from '../../components/VcnManagement/VcnManagementSearchField'
import { VcnManagementCardActions } from '../../components/VcnManagement/VcnManagementCardActions'
import { VcnManagementCardBulkUploadModal } from '../../components/VcnManagement/VcnManagementCardBulkUploadModal'
import { VcnBulkJobState } from '../../api/dto/vcn-bulk-job.dto'
import type { VcnBulkJobStatus } from '../../api/dto/vcn-bulk-job.dto'
import type {
  PaginationState,
  VcnCardListSort,
  VcnManagementCardFilters,
  VcnManagementCardRow,
} from '../../components/VcnManagement/types'
import { exportVcnManagementCards } from '../../api/vcn-management-api-client'
import { useVcnManagementCardList } from '../../components/VcnManagement/hooks/useVcnManagementCardList'
import { useStickyRows } from '../../components/VcnManagement/hooks/useStickyRows'
import { reconcilePageSelection } from '../../components/VcnManagement/hooks/usePaginatedSelection'
import { useVcnCardMetrics } from '../../components/VcnManagement/hooks/useVcnCardMetrics'
import { useInvalidateVcnData } from '../../components/VcnManagement/hooks/useInvalidateVcnData'
import { VcnManagementMetricTiles } from '../../components/VcnManagement/VcnManagementMetricTiles'
import { VcnManagementMetricBanner } from '../../components/VcnManagement/VcnManagementMetricBanner'
import {
  buildCardMetricTiles,
  cardFiltersForMetric,
  CARD_METRIC_LABELS,
  isCardMetricKey,
  VcnCardMetricKey,
} from '../../components/VcnManagement/vcnManagementCardMetrics'
import {
  countVcnManagementCardActiveFilters,
  DEFAULT_VCN_MANAGEMENT_CARD_FILTERS,
} from '../../components/VcnManagement/vcnManagementFilterDefaults'
import {
  Container,
  LeftSection,
  RightSection,
} from '../../components/VcnManagement/VcnManagementTopbar.styles'
import {
  ContentArea,
  LoadingOverlay,
  Placeholder,
} from '../../components/VcnManagement/VcnManagementLayout.styles'

/**
 * Card Mode — one row per VCN card, both providers, all card statuses.
 *
 * Holds its own filter and pagination state rather than sharing Benefit Mode's:
 * the two filter sets genuinely differ (this one has `inUse` and the card's own
 * status), so carrying state across a tab switch would change what a filter
 * means. The tab is unmounted when inactive, so this state resets by design.
 *
 * Tiles arrive in a later batch; this is the table, its filters and its
 * card-keyed selection.
 */
export interface VcnManagementCardTabProps {
  /** Bulk-job state is owned by the page: a job outlives a tab switch, and the
   * inactive pane is unmounted. */
  bulkJob: VcnBulkJobStatus | null
  isJobActive: boolean
  refreshBulkJob: () => Promise<unknown> | void
  onShowJobProgress: () => void
}

export function VcnManagementCardTab({
  bulkJob,
  isJobActive,
  refreshBulkJob,
  onShowJobProgress,
}: VcnManagementCardTabProps) {
  const [filters, setFilters] = useState<VcnManagementCardFilters>(() => ({
    ...DEFAULT_VCN_MANAGEMENT_CARD_FILTERS,
  }))
  const [searchInput, setSearchInput] = useState('')
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  // A metric filter is special: it REPLACES the sidebar filters rather than
  // narrowing within them, because the tile counts the whole universe. Held
  // separately from `filters` so the grid query stays plain filters — the number
  // and the rows agree because EPS classifies both the same way, not because a
  // metric travels to the server. See `cardFiltersForMetric`.
  const [activeMetric, setActiveMetric] = useState<VcnCardMetricKey | null>(
    null
  )
  // Selection is held here, not in the grid: it spans pagination pages, and the
  // grid only ever holds the current one. See `usePaginatedSelection`.
  const [selectedRows, setSelectedRows] = useState<VcnManagementCardRow[]>([])
  // Null means the server's default, newest first — a distinct state from being
  // sorted by created date, which has no column.
  const [activeSort, setActiveSort] = useState<VcnCardListSort | null>(null)
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 25,
    total: 0,
  })

  // The row a page number pointed at is meaningless once the order changes.
  const handleSortChange = useCallback((next: VcnCardListSort | null) => {
    setActiveSort(next)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }, [])

  const {
    data: cardMetrics,
    isPending: isMetricsLoading,
    error: metricsError,
  } = useVcnCardMetrics()

  const {
    data,
    isPending,
    isFetching,
    isPlaceholderData,
    error: queryError,
  } = useVcnManagementCardList(
    filters,
    pagination.page,
    pagination.pageSize,
    activeSort
  )

  const invalidateVcnData = useInvalidateVcnData()
  const error = queryError instanceof Error ? queryError : null

  // Rows already on screen have to survive a failed page or filter fetch.
  // `keepPreviousData` does not cover this: the placeholder applies only while
  // the new key's query is PENDING, so the moment that query errors its data is
  // undefined and the grid would be replaced by an error pane mid-session. A
  // same-key Refresh failure keeps its rows from the cache, which is what made
  // this look handled. Mirrors Benefit Mode's `lastNonEmptyRowsRef`.
  const { rows, hasEverHadRows, isShowingPreviousRows } = useStickyRows(
    data?.items ?? [],
    error
  )

  // Omitted whenever the rows on screen are not this page's: while
  // keepPreviousData is still showing the old page, and — the case this missed
  // — after a failed page fetch, when React Query has dropped the placeholder
  // but useStickyRows is holding the previous rows. Recording those under the
  // new page's key made the next successful load look like a refetch of it, and
  // reconcilePageSelection then dropped the earlier page's ticks.
  // Sort is part of the page's identity, not just paging. Re-sorting reorders
  // the same filtered set, so a card ticked on page 1 that moves to page 3 is
  // still a valid pick — exactly like turning a page, which already keeps it.
  // Without the sort segment a re-sort on page 1 looks like a refetch of page
  // 1, and reconcilePageSelection prunes every tick whose row moved off it.
  // `null` is the server default, a state no real sort can spell.
  const sortKey = activeSort
    ? `${activeSort.sort}:${activeSort.order}`
    : 'default'
  const pageKey =
    isPlaceholderData || isShowingPreviousRows
      ? undefined
      : `${sortKey}:${pagination.page}:${pagination.pageSize}`
  const prevPageKeyRef = useRef<string | undefined>(undefined)
  const prevRowKeysRef = useRef<Set<string>>(new Set())

  // Refetch keeps the same keys ticked while replacing the row objects behind
  // them. Terminate eligibility is derived from those objects, so reconcile
  // here — the selection owner — the same way Associated VCNs prunes after
  // refetch. Omit pageKey while keepPreviousData is still showing the previous
  // page, or a page turn looks like a refetch and drops the prior page's ticks.
  useEffect(() => {
    const result = reconcilePageSelection({
      rows,
      selectedRows,
      keyOf: cardKeyOf,
      pageKey,
      prevPageKey: prevPageKeyRef.current,
      prevRowKeys: prevRowKeysRef.current,
    })
    prevPageKeyRef.current = result.pageKey
    prevRowKeysRef.current = result.rowKeys
    if (result.selectedRows) setSelectedRows(result.selectedRows)
  }, [pageKey, rows, selectedRows])
  // `isPending` is only true before the first page ever arrives. Once it has,
  // paging shows the previous page under a dimming overlay instead of a
  // spinner where the table used to be.
  const isPagingOrRefetching = isFetching || isPlaceholderData

  // `total` is the filtered count before paging, so a shrinking result set can
  // put the current page past the end — fall back to the first page rather than
  // showing an empty grid with rows available. Depends on `pageSize` too: that
  // also changes the page count, and without it this never re-runs for a size
  // change.
  useEffect(() => {
    if (data?.total === undefined) return
    const total = data.total
    setPagination((prev) => {
      const maxPage = Math.max(1, Math.ceil(total / prev.pageSize))
      const page = prev.page > maxPage ? 1 : prev.page
      if (prev.total === total && prev.page === page) return prev
      return { ...prev, total, page }
    })
  }, [data?.total, pagination.pageSize])

  // A page-size change resets to page 1, matching Benefit Mode. Page 10 of 25
  // is not page 10 of 200, so keeping the number can request a slice past the
  // end — and the `total` guard below cannot rescue it, because changing the
  // size leaves `total` untouched.
  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setPagination((prev) => ({
        ...prev,
        page: pageSize !== prev.pageSize ? 1 : page,
        pageSize,
      }))
    },
    []
  )

  // Any filter change resets to page 1: the row a page number pointed at is
  // meaningless once the filtered set changes underneath it.
  // Selection is dropped with the filtered set, not carried across it: a card
  // selected under one filter is off-screen and unverifiable under the next,
  // and a terminate must only ever hit cards the admin can still see listed.
  const handleSearchSubmit = useCallback((value: string) => {
    const search = value.trim() || undefined
    setFilters((prev) => ({ ...prev, search }))
    setPagination((prev) => ({ ...prev, page: 1 }))
    setSelectedRows([])
  }, [])

  const handleSelectMetric = useCallback(
    (key: string) => {
      if (!isCardMetricKey(key)) return
      // Re-clicking the active number is a no-op; clearing goes through the
      // banner. Same rule as Benefit Mode, so the two tabs behave alike.
      if (key === activeMetric) return
      setActiveMetric(key)
      setFilters(cardFiltersForMetric(key))
      setSearchInput('')
      setFilterDrawerOpen(false)
      setPagination((prev) => ({ ...prev, page: 1 }))
      setSelectedRows([])
    },
    [activeMetric]
  )

  const handleClearMetric = useCallback(() => {
    setActiveMetric(null)
    setFilters({ ...DEFAULT_VCN_MANAGEMENT_CARD_FILTERS })
    setSearchInput('')
    setPagination((prev) => ({ ...prev, page: 1 }))
    setSelectedRows([])
  }, [])

  const handleSidebarApply = useCallback((next: VcnManagementCardFilters) => {
    setFilters(next)
    setPagination((prev) => ({ ...prev, page: 1 }))
    setSelectedRows([])
  }, [])

  // Refresh invalidates the list and the tiles together. `refetch()` on the
  // list query alone would leave the numbers serving cached card metrics for
  // the rest of staleTime — the mismatch this work is meant to prevent.
  const handleRefresh = useCallback(() => {
    invalidateVcnData()
  }, [invalidateVcnData])

  const clearSelection = useCallback(() => setSelectedRows([]), [])

  /* ----------------------------- Bulk terminate -----------------------------
   * Only one bulk job runs platform-wide, shared with Benefit Mode. When one is
   * already going, the button shows its progress rather than opening an upload
   * that would be refused on submit.
   */
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false)

  const handleBulkUpload = useCallback(() => {
    if (isJobActive || bulkJob?.state === VcnBulkJobState.TIMEOUT) {
      onShowJobProgress()
    } else {
      setBulkUploadOpen(true)
    }
  }, [isJobActive, bulkJob?.state, onShowJobProgress])

  const handleJobStarted = useCallback(() => {
    setBulkUploadOpen(false)
    onShowJobProgress()
    void refreshBulkJob()
    // The job changes what both tabs list and what the tiles count.
    invalidateVcnData()
  }, [invalidateVcnData, onShowJobProgress, refreshBulkJob])

  /* -------------------------------- Export ---------------------------------
   * Server-side, and unpaged: the page cap is 200, so building the file from
   * list requests would mean ~90 round trips for an unfiltered export. It sends
   * whatever filters are active, a metric included, so "filter to the orphans,
   * export them" is one action rather than a reconstruction.
   */
  const [isExporting, setIsExporting] = useState(false)

  /**
   * Cancels an export still in flight when this tab goes away.
   *
   * The inactive pane is unmounted (`destroyInactiveTabPane`), so without this
   * the request outlives the state tracking it: coming back showed Export idle
   * while the first call was still running, and a second click started another
   * unpaged export against the same endpoint. Cancelling is the right end of
   * that — an export nobody is waiting for should not go on enriching every
   * card server-side.
   *
   * A CancelToken, not an AbortController: axios 0.17 ignores `signal`
   * outright, so an abort would have looked wired up and done nothing.
   */
  const exportCancelRef = useRef<CancelTokenSource | null>(null)
  useEffect(
    () => () => {
      exportCancelRef.current?.cancel()
    },
    []
  )

  const handleExport = useCallback(() => {
    exportCancelRef.current?.cancel()
    const source = axios.CancelToken.source()
    exportCancelRef.current = source
    setIsExporting(true)
    // The whole row set is built before a single byte is sent, so there is no
    // progress to report — only a spinner, and a large export can hold it for
    // tens of seconds.
    void exportVcnManagementCards(filters, source.token)
      .then(({ fileName }) => {
        void message.success(`Exported ${fileName}`)
      })
      .catch((error: unknown) => {
        // A cancel is this component leaving, not a failure the admin caused.
        if (axios.isCancel(error)) return
        void message.error('Export failed. Please try again.')
      })
      .finally(() => {
        if (source.token.reason) return
        setIsExporting(false)
      })
  }, [filters])

  const activeFilterCount = useMemo(
    () => countVcnManagementCardActiveFilters(filters),
    [filters]
  )

  const metricTiles = useMemo(
    () => buildCardMetricTiles(cardMetrics),
    [cardMetrics]
  )

  const tiles = (
    <VcnManagementMetricTiles
      tiles={metricTiles}
      activeKey={activeMetric}
      isLoading={isMetricsLoading}
      error={metricsError instanceof Error ? metricsError : null}
      onSelect={handleSelectMetric}
    />
  )

  const metricBanner = activeMetric ? (
    <VcnManagementMetricBanner
      label={CARD_METRIC_LABELS[activeMetric]}
      onClear={handleClearMetric}
    />
  ) : null

  // While a metric filter is active the ordinary filters are disabled: the two
  // are mutually exclusive by design, because a tile's number counts the whole
  // universe and narrowing it further would leave the count describing rows that
  // are no longer on screen. Clearing the metric re-enables them.
  const toolbar = (
    <Container>
      <LeftSection>
        <VcnManagementSearchField
          value={searchInput}
          onChange={setSearchInput}
          onSearch={handleSearchSubmit}
          isDisabled={activeMetric !== null}
          placeholder="Search Token ID, last 4, Benefit ID, or Insured name"
        />
      </LeftSection>
      <RightSection>
        {/* Icon trails the label, and Refresh/Filters are icon-only squares —
            the design's toolbar, see VCN Bulk Termination.dc.html. */}
        <Button
          className="vcn-icon-only"
          title="Refresh"
          aria-label="Refresh"
          onClick={handleRefresh}
        >
          <ReloadOutlined />
        </Button>
        <Button
          disabled={isExporting || rows.length === 0}
          onClick={handleExport}
        >
          Export
          {isExporting ? <LoadingOutlined spin /> : <DownloadOutlined />}
        </Button>
        <Button onClick={handleBulkUpload}>
          Bulk terminate
          {isJobActive ? <LoadingOutlined spin /> : <UploadOutlined />}
        </Button>
        <Badge count={activeMetric ? 0 : activeFilterCount} offset={[-5, 5]}>
          <Button
            className="vcn-icon-only"
            title="Filters"
            aria-label="Filters"
            disabled={activeMetric !== null}
            onClick={() => setFilterDrawerOpen(true)}
          >
            <FilterOutlined />
          </Button>
        </Badge>
      </RightSection>
    </Container>
  )

  const bulkUploadModal = (
    <VcnManagementCardBulkUploadModal
      open={bulkUploadOpen}
      onClose={() => setBulkUploadOpen(false)}
      onJobStarted={handleJobStarted}
    />
  )

  const sidebar = (
    <VcnManagementCardSidebar
      open={filterDrawerOpen}
      onClose={() => setFilterDrawerOpen(false)}
      appliedFilters={filters}
      onApply={handleSidebarApply}
    />
  )

  if (isPending) {
    return (
      <>
        {tiles}
        {metricBanner}
        {toolbar}
        <ContentArea>
          <Placeholder>
            <Spin indicator={<LoadingOutlined spin />} />
          </Placeholder>
        </ContentArea>
        {sidebar}
        {bulkUploadModal}
      </>
    )
  }

  // Only take over the whole pane when nothing has ever been shown. Once any
  // page has loaded, a later failure keeps those rows under a warning instead
  // of replacing them — same behaviour as Benefit Mode.
  if (error && !hasEverHadRows) {
    return (
      <>
        {tiles}
        {metricBanner}
        {toolbar}
        <ContentArea>
          <Alert
            type="error"
            showIcon
            message="Could not load VCN cards"
            description={error.message}
          />
        </ContentArea>
        {sidebar}
        {bulkUploadModal}
      </>
    )
  }

  return (
    <>
      {tiles}
      {metricBanner}
      {toolbar}
      {error ? (
        <Alert
          type="warning"
          showIcon
          message="Could not refresh VCN cards — showing the last loaded page"
          description={error.message}
        />
      ) : null}
      <ContentArea>
        {isPagingOrRefetching ? (
          <LoadingOverlay>
            <Spin indicator={<LoadingOutlined spin />} />
          </LoadingOverlay>
        ) : null}
        <VcnManagementCardTable
          rows={rows}
          emptyText={<Empty description="No VCN cards found" />}
          selectedRows={selectedRows}
          pagination={pagination}
          activeSort={activeSort}
          onSortChange={handleSortChange}
          onRowSelectionChange={setSelectedRows}
          onPaginationChange={handlePaginationChange}
        />
      </ContentArea>
      {/* Portals to document.body — placement here is for ownership, not layout. */}
      <VcnManagementCardActions
        selectedRows={selectedRows}
        onClearSelection={clearSelection}
        isFetching={isPagingOrRefetching}
      />
      {sidebar}
      {bulkUploadModal}
    </>
  )
}
